import { useQuery } from "@tanstack/react-query"
import { useRef } from "react"
import { getSocialPostStatus } from "../services/social-publish.service"

const POLL_INTERVAL_MS = 2000
const POLL_TIMEOUT_MS = 60_000

/**
 * Polls a social post's status every 2s until it reaches a terminal state
 * or 60 seconds have elapsed, whichever comes first.
 */
export function useSocialPostStatus(postId: string | null) {
  const startedAtRef = useRef<number>(0)

  // Only capture the start time when postId first becomes non-null
  if (postId && startedAtRef.current === 0) {
    startedAtRef.current = Date.now()
  }

  const query = useQuery({
    queryKey: ["social-post-status", postId],
    queryFn: () => {
      if (!postId) throw new Error("postId is required")
      return getSocialPostStatus(postId)
    },
    enabled: !!postId,
    refetchInterval: query => {
      const status = query.state.data?.status
      if (status === "published" || status === "failed") return false
      if (Date.now() - startedAtRef.current > POLL_TIMEOUT_MS) return false
      return POLL_INTERVAL_MS
    },
    staleTime: 0,
  })

  const timedOut =
    !!postId &&
    startedAtRef.current > 0 &&
    Date.now() - startedAtRef.current > POLL_TIMEOUT_MS &&
    query.data?.status !== "published" &&
    query.data?.status !== "failed"

  return { ...query, timedOut }
}
